import { useEffect, useMemo, useRef, useState } from 'react'
import * as customersApi from '@/api/customers'

const WORKSTATION_COLUMNS = [
  { key: 'hostname', label: 'Hostname' },
  { key: 'display_name', label: 'Display name' },
  { key: 'folder', label: 'Folder' },
  { key: 'os', label: 'OS' },
  { key: 'ip', label: 'IP' },
  { key: 'domain', label: 'Domain' },
  { key: 'last_user', label: 'Last user' },
]

const SERVER_COLUMNS = [
  { key: 'hostname', label: 'Hostname' },
  { key: 'display_name', label: 'Display name' },
  { key: 'os', label: 'OS' },
  { key: 'ip', label: 'IP' },
]

const FOLDER_OPTIONS = ['Workstation', 'Laptop']

/** Prefer Dash folder_name (Atera Folder); fall back to machine_type. */
export function folderBadgeLabel(folderName, machineType) {
  function fromText(value) {
    const raw = String(value || '')
      .trim()
      .toLowerCase()
    if (!raw) return ''
    if (raw.includes('laptop') || raw.includes('notebook')) return 'Laptop'
    if (raw.includes('workstation') || raw.includes('desktop')) {
      return 'Workstation'
    }
    return ''
  }

  // Folder is the staff-maintained label in Dash — do not let machine_type override it.
  const fromFolder = fromText(folderName)
  if (fromFolder) return fromFolder

  const fromType = fromText(machineType)
  if (fromType) return fromType

  if (folderName && String(folderName).trim()) {
    return String(folderName).trim()
  }
  return ''
}


function FolderBadge({ label }) {
  if (!label) return <span className="text-brand-muted">—</span>
  const isLaptop = label.toLowerCase() === 'laptop'
  const isWorkstation = label.toLowerCase() === 'workstation'
  const tone = isLaptop
    ? 'border-sky-300 bg-sky-100 text-sky-900'
    : isWorkstation
      ? 'border-slate-300 bg-slate-100 text-slate-800'
      : 'border-slate-200 bg-slate-50 text-brand-muted'
  return (
    <span
      className={[
        'inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        tone,
      ].join(' ')}
    >
      {label}
    </span>
  )
}

function ensureColumn(columns, { key, label }, afterKey = 'hostname') {
  if (columns.some((c) => c.key === key)) return columns
  const cols = columns.map((c) => ({ ...c }))
  const hostIdx = cols.findIndex((c) => c.key === afterKey)
  cols.splice(hostIdx >= 0 ? hostIdx + 1 : 0, 0, { key, label })
  return cols
}

function columnsForSlug(slug, categoryColumns) {
  const fallback = slug === 'servers' ? SERVER_COLUMNS : WORKSTATION_COLUMNS
  if (!categoryColumns?.length) return fallback
  let cols = categoryColumns.map((c) => ({ ...c }))
  // Older DBs may lack these until migrations 026/027.
  if (slug === 'workstations' || slug === 'servers') {
    cols = ensureColumn(cols, {
      key: 'display_name',
      label: 'Display name',
    })
  }
  if (slug === 'workstations') {
    cols = ensureColumn(cols, { key: 'folder', label: 'Folder' }, 'display_name')
  }
  return cols
}


function emptyDraft(columns) {
  const values = {}
  for (const col of columns) {
    values[col.key] = col.key === 'folder' ? 'Workstation' : ''
  }
  return values
}

function dashToValues(d) {
  return {
    hostname:
      d.hostname || (d.atera_id ? `Agent ${d.atera_id}` : ''),
    display_name: d.display_name || '',
    folder: folderBadgeLabel(d.folder_name, d.machine_type),
    type: d.machine_type || '',
    os: [d.os_name, d.os_version].filter(Boolean).join(' '),
    ip: d.reported_from_ip || '',
    domain: d.domain || '',
    last_user: d.last_logged_user || '',
  }
}

function compareValues(a, b, key) {
  const av = (a.values?.[key] ?? '').toString()
  const bv = (b.values?.[key] ?? '').toString()
  return av.localeCompare(bv, undefined, {
    sensitivity: 'base',
    numeric: true,
  })
}

export function DeviceList({
  customerId,
  category,
  customer,
  canEdit,
  onCustomerUpdated,
}) {
  const columns = columnsForSlug(category.slug, category.columns)
  const [dashDevices, setDashDevices] = useState([])
  const [manualRows, setManualRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dashCustomers, setDashCustomers] = useState([])
  const [linking, setLinking] = useState(false)
  const [selectedDashId, setSelectedDashId] = useState(
    customer?.dash_atera_id || '',
  )
  const [draft, setDraft] = useState(() => emptyDraft(columns))
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null)
  const [refreshNotice, setRefreshNotice] = useState('')
  const [sortKey, setSortKey] = useState('hostname')
  const [sortDir, setSortDir] = useState('asc')
  const refreshNoticeTimer = useRef(null)

  const linked = Boolean(customer?.dash_atera_id)
  const columnKeys = useMemo(() => columns.map((c) => c.key).join(','), [columns])

  const displayRows = useMemo(() => {
    const fromDash = dashDevices.map((d) => ({
      id: `dash-${d.atera_id}`,
      source: 'dash',
      values: dashToValues(d),
    }))
    const fromManual = manualRows.map((r) => ({
      id: r.id,
      source: 'manual',
      values: { ...emptyDraft(columns), ...(r.values || {}) },
    }))
    const merged = [...fromDash, ...fromManual]
    const key = columns.some((c) => c.key === sortKey) ? sortKey : 'hostname'
    const dir = sortDir === 'desc' ? -1 : 1
    return merged.sort((a, b) => dir * compareValues(a, b, key))
  }, [dashDevices, manualRows, columns, sortKey, sortDir])

  const dashCount = dashDevices.length
  const manualCount = manualRows.length
  const totalCount = displayRows.length
  const itemLabel =
    category.slug === 'servers'
      ? totalCount === 1
        ? 'server'
        : 'servers'
      : totalCount === 1
        ? 'workstation'
        : 'workstations'

  useEffect(() => {
    setSelectedDashId(customer?.dash_atera_id || '')
  }, [customer?.dash_atera_id])

  useEffect(() => {
    setDraft(emptyDraft(columns))
  }, [category.slug, columnKeys])

  useEffect(() => {
    return () => {
      if (refreshNoticeTimer.current) clearTimeout(refreshNoticeTimer.current)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setError('')
      setLoading(true)

      if (canEdit) {
        try {
          const list = await customersApi.listDashCustomers()
          if (!cancelled) setDashCustomers(list)
        } catch (err) {
          if (!cancelled) {
            setError(
              typeof err.response?.data?.detail === 'string'
                ? err.response.data.detail
                : 'Could not load Dash customers. Check Dash credentials in the KB .env.',
            )
          }
        }
      }

      try {
        const [devices, rows] = await Promise.all([
          linked
            ? customersApi.listDevices(customerId, category.slug).catch(() => [])
            : Promise.resolve([]),
          customersApi.listRows(customerId, category.slug),
        ])
        if (!cancelled) {
          setDashDevices(devices)
          setManualRows(rows)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load devices.',
          )
          setDashDevices([])
          setManualRows([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [customerId, category.slug, linked, canEdit])

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  async function handleLink(e) {
    e.preventDefault()
    setLinking(true)
    setError('')
    try {
      const dashId =
        selectedDashId === '' || selectedDashId === null
          ? null
          : Number(selectedDashId)
      const updated = await customersApi.updateCustomer(customerId, {
        dash_atera_id: dashId,
      })
      onCustomerUpdated?.(updated)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not link Dash customer.',
      )
    } finally {
      setLinking(false)
    }
  }

  async function handleRefreshFromDash() {
    if (!linked || refreshing) return
    setRefreshing(true)
    setError('')
    setRefreshNotice('')
    try {
      const devices = await customersApi.listDevices(customerId, category.slug)
      setDashDevices(devices)
      const at = new Date()
      setLastRefreshedAt(at)
      const n = devices.length
      const label =
        category.slug === 'servers'
          ? n === 1
            ? 'server'
            : 'servers'
          : n === 1
            ? 'workstation'
            : 'workstations'
      setRefreshNotice(`Refreshed — ${n} ${label} from Dash`)
      if (refreshNoticeTimer.current) clearTimeout(refreshNoticeTimer.current)
      refreshNoticeTimer.current = setTimeout(() => setRefreshNotice(''), 4000)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not refresh devices from Dash.',
      )
    } finally {
      setRefreshing(false)
    }
  }

  function setDraftValue(key, value) {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  async function handleAdd(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const created = await customersApi.createRow(customerId, category.slug, {
        values: draft,
        notes: null,
      })
      setManualRows((prev) => [...prev, created])
      setDraft(emptyDraft(columns))
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not add device.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-8 space-y-4">
      {canEdit && (
        <form
          onSubmit={handleLink}
          className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50/80 p-4 sm:flex-row sm:items-end"
        >
          <div className="flex-1">
            <label
              htmlFor="dash-link"
              className="block text-xs font-semibold uppercase tracking-wide text-brand-muted"
            >
              Bekat Dash company
            </label>
            <select
              id="dash-link"
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={
                selectedDashId === null || selectedDashId === undefined
                  ? ''
                  : String(selectedDashId)
              }
              onChange={(e) => setSelectedDashId(e.target.value)}
            >
              <option value="">Not linked</option>
              {dashCustomers.map((c) => (
                <option key={c.atera_id} value={c.atera_id}>
                  {c.name}
                </option>
              ))}
              {linked &&
                !dashCustomers.some(
                  (c) => c.atera_id === customer.dash_atera_id,
                ) && (
                  <option value={customer.dash_atera_id}>
                    Current link (Atera {customer.dash_atera_id})
                  </option>
                )}
            </select>
          </div>
          <button
            type="submit"
            disabled={linking}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {linking ? 'Saving…' : 'Save link'}
          </button>
        </form>
      )}

      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {!linked && (
        <p className="text-sm text-brand-muted">
          Link this KB customer to a Bekat Dash company to import live{' '}
          {category.title.toLowerCase()}, or add them manually below.
        </p>
      )}

      {canEdit && (
        <form
          onSubmit={handleAdd}
          className="overflow-x-auto rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-3"
        >
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">
            Add {category.slug === 'servers' ? 'server' : 'device'}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            {columns.map((col) => (
              <div key={col.key} className="min-w-[8rem] flex-1">
                <label className="block text-xs text-brand-muted">
                  {col.label}
                </label>
                {col.key === 'folder' ? (
                  <select
                    className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    value={draft[col.key] || 'Workstation'}
                    onChange={(e) => setDraftValue(col.key, e.target.value)}
                  >
                    {FOLDER_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    value={draft[col.key] || ''}
                    onChange={(e) => setDraftValue(col.key, e.target.value)}
                    placeholder={col.label}
                  />
                )}
              </div>
            ))}
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {loading && <p className="text-sm text-brand-muted">Loading devices…</p>}

      {!loading && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <p className="text-sm font-semibold text-brand">
                {totalCount} {itemLabel}
              </p>
              {(dashCount > 0 || manualCount > 0) && (
                <p className="text-xs text-brand-muted">
                  {dashCount > 0 && (
                    <span>
                      {dashCount} from Dash
                      {manualCount > 0 ? ', ' : ''}
                    </span>
                  )}
                  {manualCount > 0 && (
                    <span>{manualCount} added manually</span>
                  )}
                </p>
              )}
            </div>
            {linked && (
              <div className="flex flex-wrap items-center gap-2">
                {refreshNotice ? (
                  <p
                    className="text-xs font-medium text-emerald-700"
                    role="status"
                  >
                    {refreshNotice}
                  </p>
                ) : lastRefreshedAt ? (
                  <p className="text-xs text-brand-muted">
                    Last refreshed{' '}
                    {lastRefreshedAt.toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={handleRefreshFromDash}
                  disabled={refreshing}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-brand hover:bg-slate-100 disabled:opacity-60"
                >
                  {refreshing ? 'Refreshing…' : 'Refresh from Dash'}
                </button>
              </div>
            )}
          </div>
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                {columns.map((col) => {
                  const active = sortKey === col.key
                  return (
                    <th
                      key={col.key}
                      className="px-3 py-2 font-semibold text-brand-muted"
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className={[
                          'inline-flex items-center gap-1 hover:text-brand',
                          active ? 'text-brand' : '',
                        ].join(' ')}
                        aria-sort={
                          active
                            ? sortDir === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        {col.label}
                        <span className="text-[10px] tabular-nums" aria-hidden>
                          {active ? (sortDir === 'asc' ? '▲' : '▼') : '◇'}
                        </span>
                      </button>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {displayRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-3 py-8 text-center text-brand-muted"
                  >
                    No {category.title.toLowerCase()} yet.
                  </td>
                </tr>
              ) : (
                displayRows.map((item) => (
                  <tr
                    key={item.id}
                    className={
                      item.source === 'manual' ? 'bg-amber-50/70' : undefined
                    }
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-3 py-2 text-brand ${
                          col.key === 'hostname' ? 'font-medium' : ''
                        } ${col.key === 'ip' ? 'font-mono text-xs' : ''}`}
                      >
                        {col.key === 'hostname' ? (
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <span>{item.values[col.key] || '—'}</span>
                            {item.source === 'manual' && (
                              <span className="rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                                Manual
                              </span>
                            )}
                          </span>
                        ) : col.key === 'folder' ? (
                          <FolderBadge label={item.values.folder || ''} />
                        ) : (
                          item.values[col.key] || '—'
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
